import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import JoinFoot from '../assets/join-foot.png';
import JoinHead from '../assets/join-head.png';
import { Layout } from '../components/page-layout';

const GlobalStyle = createGlobalStyle`
  body {
    background: #fff;
    color: #222;
    font-size: 18px;
    letter-spacing: 0;
  }
`;

const Main = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
`;

const Title = styled.h3`
  font-size: 2.2rem;
  font-weight: 800;
  color: #181818;
  margin-bottom: 1.5rem;
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: #222;
  margin-bottom: 2.5rem;
`;

const SectionFlex = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2.5rem;
  @media (min-width: 900px) {
    flex-direction: row;
    gap: 3rem;
  }
`;

const SectionImg = styled.img`
  width: 100%;
  max-width: 520px;
  height: auto;
  object-fit: contain;
`;

const Section = styled.section`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h3`
  color: #2E2E2E;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const SectionList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 900px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }
`;

const ListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const ListHeading = styled.h4`
  color: #2E2E2E;
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const ListDesc = styled.p`
  color: #7A7A7A;
  font-size: 1.05rem;
`;

const PerksFlex = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2rem;
  @media (min-width: 900px) {
    flex-direction: row;
    align-items: center;
  }
`;

const PerksList = styled.ul`
  list-style: disc inside;
  color: #222;
  font-size: 1.05rem;
  margin-top: 1rem;
  margin-bottom: 0;
  padding-left: 1.5rem;
`;

const PerksImg = styled.img`
  width: 100%;
  min-width: 335px;
  max-width: 580px;
  height: auto;
  object-fit: contain;
`;

const CTASection = styled.div`
  padding: 2.5rem 1.5rem;
  margin-top: 3rem;
  background: #F87B15;
  color: #fff;
  border-radius: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const CTATitle = styled.h3`
  color: #fff;
  font-size: 1rem;
  font-weight: 400;
  margin-bottom: 0.5rem;
`;

const CTADesc = styled.p`
  color: #fff;
  text-align: center;
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.25;
  margin-bottom: 0.5rem;
`;

const CTAButton = styled.a`
  display: inline-block;
  background: #fff;
  color: #F87B15;
  border-radius: 999px;
  padding: 0.75rem 2.5rem;
  font-weight: 700;
  font-size: 1.1rem;
  text-decoration: none;
  margin-top: 1.5rem;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #ffe3c2;
    color: #d96a10;
  }
`;

export default function CareersPage() {
  return (
    <Layout showCommonFooter>
      <GlobalStyle />
      <Main>
        <Title>Join the Sensforge Team</Title>
        <Description>
          At Sensforge, we&apos;re on a mission to make innovative technology accessible to everyone, and we’re looking for passionate, driven individuals to help us shape the future of smart home solutions.
        </Description>
        <SectionFlex>
          <SectionImg src={JoinHead} alt="Join head" />
        </SectionFlex>
        <Section>
          <SectionTitle>Why Work With Us?</SectionTitle>
          <SectionList>
            <ListItem>
              <ListHeading>Innovative Environment</ListHeading>
              <ListDesc>We thrive on creativity and encourage bold ideas that push boundaries.</ListDesc>
            </ListItem>
            <ListItem>
              <ListHeading>Collaborative Culture</ListHeading>
              <ListDesc>Teamwork drives us—great solutions come from collaboration.</ListDesc>
            </ListItem>
            <ListItem>
              <ListHeading>Growth Opportunities</ListHeading>
              <ListDesc>We’re committed to helping you grow—both personally and professionally—with mentorship, learning resources, and clear paths for advancement.</ListDesc>
            </ListItem>
            <ListItem>
              <ListHeading>Real Impact</ListHeading>
              <ListDesc>Your work will directly shape products that enhance lives worldwide, making a meaningful impact on countless people every day.</ListDesc>
            </ListItem>
          </SectionList>
        </Section>
        <PerksFlex>
          <div>
            <SectionTitle>Perks & Benefits</SectionTitle>
            <PerksList>
              <li>Flexible work options (remote & hybrid opportunities)</li>
              <li>Comprehensive health, dental, and vision coverage</li>
              <li>Generous PTO and paid holidays</li>
              <li>Professional development stipends</li>
              <li>Employee discounts on Sensforge products</li>
            </PerksList>
          </div>
          <PerksImg src={JoinFoot} alt="Looking Ahead" />
        </PerksFlex>
        <CTASection>
          <CTATitle>Current Openings</CTATitle>
          <CTADesc>
            We&apos;re always on the lookout for talented individuals who are excited <br />
            about technology and innovation. Whether you&apos;re a seasoned <br />
            professional or just starting your career, <br />
            there&apos;s a place for you at SensForge.<br />
          </CTADesc>
          <CTADesc>
            Don’t see a role that fits? <br />
            We’d still love to hear from you! <br />
            Send us your resume at careers@sensforge.com.<br />
            Come build the future with us!<br />
          </CTADesc>
          <CTAButton
            href="mailto:careers@sensforge.com"
            type="button"
          >
            View All Openings
          </CTAButton>
        </CTASection>
      </Main>
    </Layout>
  );
}
